import { useState, type ReactNode } from "react";
import ViewSwitcher from "@/components/UI/TableToolbar/ViewSwitcher";
import TableToolbar from "@/components/UI/TableToolbar/TableToolbar";
import useIsFeatureFlagEnabled from "@/hooks/useIsFeatureFlagEnabled";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

type Props = {
  tableContent: ReactNode;
  dashboardContent: ReactNode;
  timelineContent?: ReactNode;
};

export default function SiteTabTemplate({
  tableContent,
  dashboardContent,
  timelineContent,
}: Props) {
  const isFlagEnabled = useIsFeatureFlagEnabled();
  // Dashboard-only tenants drop the Table view, but only on tabs that have a
  // dashboard to show instead — a tab whose dashboard is empty (e.g. Labs)
  // keeps its table rather than rendering a blank page.
  const dashboardOnly =
    isFlagEnabled(FEATURE_FLAGS.SITE_PROFILE_DASHBOARD_ONLY) &&
    dashboardContent != null;

  const options = [
    ...(dashboardOnly ? [] : [{ label: "Table", value: "table" }]),
    { label: "Dashboard", value: "plots" },
    ...(timelineContent ? [{ label: "Timeline", value: "timeline" }] : []),
  ];

  const [view, setView] = useState(options[0].value);
  const activeView = options.some((o) => o.value === view)
    ? view
    : options[0].value;

  return (
    <div className="w-full flex flex-col gap-4">
      {options.length > 1 && (
        <TableToolbar
          viewSwitcher={
            <ViewSwitcher
              screen={activeView}
              setScreen={setView}
              options={options}
            />
          }
        />
      )}

      {activeView === "table" && tableContent}
      {activeView === "plots" && dashboardContent}
      {activeView === "timeline" && timelineContent}
    </div>
  );
}
